import { Component, Input, Output, OnInit, OnChanges, OnDestroy, EventEmitter, Inject, ElementRef } from '@angular/core';
import { availableBrandGroups, eroiIds, EroiIdToPillarMapping, ModalActions, tvdData, TvdDrillDownGrouping, TvdMetricDisplayData, TVDMetricDrilldownDetails, TvdValuePillar } from '../../types/tvd-types';
import { eroiService } from '../../services/eroi-metric.service';
import { MixpanelService } from "../../../../core/services/mixpanel.service";
import { ContractModel } from 'src/app/global/contract-filter-model';
import { Subscription } from 'rxjs';
import { FiltersService } from 'src/app/core/services/filters.service';
import { Router } from '@angular/router';
import { TVDService } from '../../services/tvd.service';
import { TvdCostAssumptionsService } from '../../services/tvd-cost-assumptions.service';
import { CurrencyUomService, UomOption } from 'src/app/core/services/currency-uom.service';
import { UIPreferencesService } from 'src/app/core/services/ui-preferences.service';
import { ParseNumberPipe } from 'src/app/core/pipes/parse-number.pipe';
import { TooltipService } from 'src/app/shared/services/tooltip.service';
import { CostAssumptionsEligibilityResolver } from '../../resolvers/cost-assumptions-eligibility.resolver';


@Component({
    selector: 'eroi-metric',
    templateUrl: './eroi-metric.component.html',
    styleUrls: ['./eroi-metric.component.scss', '../../styles/tvd-repeat-styles.scss'],
    providers: [ParseNumberPipe],
    standalone: false
})
export class EroiMetricComponent implements OnInit, OnChanges, OnDestroy {
  filters: ContractModel | undefined;
  private filtersSubscription: Subscription | undefined;
  private currencySubscription: Subscription | undefined;
  private displayInRoundedNumberSubscription: Subscription | undefined;
  private showInEnterpriseTermsSubscription: Subscription | undefined;
  private tooltipSub: any;
  private disableSelectionButtonSubscription: Subscription | undefined;

  //sending ID as an input in addition to assigning HTML id so doesn't interfere with HTML id.
  @Input() metricId: string | undefined= "";
  @Input() groupId: string | undefined = "";
  @Input() makingSelectionsState: boolean | undefined;
  @Input() selectionMapping: Map<string, [data:tvdData | undefined, isSelected:boolean, isAddedToPres:boolean]> | undefined;
  @Input() data: tvdData | undefined;
  @Input() brandGroup: availableBrandGroups | undefined;
  @Input() brands: string[] | undefined;
  @Input() isCostAssumptionsPreview: boolean | undefined = false;
  @Output() selected = new EventEmitter<void>();
  @Output() unselected = new EventEmitter<void>();
  @Input() isSubComponent: boolean = false; // Signifies if this tile is a subcomponent of a composite metric which influences styling
  @Input() measurementSystem: UomOption = 'Imperial';

  //id maps to both title and units
  title: string | undefined = "title"; // TODO: Remove once all references are modified to pass the EROI ID instead of the title to avoid the need for mapping in this component and spec test modified.
  costCurrencyCode: string | undefined = "USD";
  selectedItem : boolean | undefined = false;
  addedToPres : boolean | undefined = false;
  iconPlusHover : boolean = false;
  isDefaultCostAssumptionsVisible: boolean = false;
  defaultCostCenterMetrics = ['Productivity','Energy','Water','No Charge Additional Services','General Contractor (GC) Cost Avoidance',
    'Set-up Services','Pest Prevention Findings','Room Nights Saved','Furniture Preservation (Assets)','Furniture Preservation (Energy)'
  ];

  // Disable the select button for adding to the presentation
  // if the limit of selected metrics per value pillar is reached and this.selectedItem==false
  disableSelectionButton: boolean = false;

  showTooltip: boolean = false;
  tooltipId: string = '';
  showEllipsis: boolean = false;
  selectedDivision: string = '';
  selectedDataView: string = '';
  isCostAssumptionsEditable: boolean = false;

  displayInRoundedNumber: boolean = false;
  showInEnterpriseTerms: boolean = true;

  TvdDrillDownGrouping = TvdDrillDownGrouping;

  // TODO: Change title to EROID and remove mapIdToTitle calls once all references are modified to pass the EROI ID
  private distributorExcludedTitles = [
    'TOTAL CASES', 'Total Customers', 'TOTAL SALES ($)',
    'Case Growth', 'Sales Growth', 'Customer +/-'
  ];
  isContractView(): boolean {
    return this.selectedDivision === 'Institutional' && this.selectedDataView === 'Contract';
  }
  isDistributorView(): boolean {
    // TODO: Update to use EROI ID instead of title
    const title = this.eroiService.mapIdToTitle(this.metricId);
    return this.selectedDivision === 'Distributor' && title !== undefined && !this.distributorExcludedTitles.includes(title);
  }
  isPestView(): boolean {
    return this.selectedDivision === 'Pest';
  }
  isViewDetailsEnabled(division:string){
    let otherEnabledDivisions = ["QSR", "FRS"];
    // TODO: Update to use EROI ID instead of title
    const title = this.eroiService.mapIdToTitle(this.metricId);
    return (this.isContractView() || this.isDistributorView() || this.isPestView() || otherEnabledDivisions.includes(division)) && title !== undefined;
  }

  isDrawerOpen : boolean = false;

  get metricData(): TvdMetricDisplayData {
    // TODO: Use TvdMetricDisplayDataBuilder to build the TvdMetricDisplayData object.
    const isMonetaryContributionModified = Number((this.data?.modifiedMonetaryContribution || '0').replace(/,/g, '')) > 0;
    const isValueContributionModified = Number((this.data?.modifiedValueContribution || '0').replace(/,/g, '')) > 0;

    return {
      currentMonetaryContribution: isMonetaryContributionModified
        ? this.data?.modifiedMonetaryContribution || '0'
        : this.data?.monetaryContribution || '0',
      defaultMonetaryContribution: isMonetaryContributionModified
        ? this.data?.monetaryContribution || '0'
        : undefined,
      currentValueContribution: isValueContributionModified
        ? this.data?.modifiedValueContribution || '0'
        : this.data?.valueContribution || '0',
      defaultValueContribution: isValueContributionModified
        ? this.data?.valueContribution || '0'
        : undefined,
      isMonetaryContributionModified,
      isValueContributionModified
    };
  }

  constructor(
    private eroiService: eroiService,
    private filtersService: FiltersService,
    private _mixpanelService: MixpanelService,
    private _tvdCostAssumptionsService: TvdCostAssumptionsService,
    private _router: Router,
    private _tvdService: TVDService,
    private _currencyUomService: CurrencyUomService,
    private _uiPreferencesService: UIPreferencesService,
    private _parseNumberPipe: ParseNumberPipe,
    private _tooltipService: TooltipService,
    private _costAssumptionsEligibilityResolver: CostAssumptionsEligibilityResolver
    ) {  }

  ngOnInit(): void {
    this.filtersSubscription = this.filtersService.selectedFiltersObservable.subscribe(f => this.filters = f);
    this.displayInRoundedNumberSubscription = this._uiPreferencesService.displayRoundedValuesObservable.subscribe(f => this.displayInRoundedNumber = f);
    this.showInEnterpriseTermsSubscription = this._tvdService.showInEnterpriseTermsObservable.subscribe(f => this.showInEnterpriseTerms = f);

    if(this.filters) {
      this.title = this.eroiService.mapIdToTitle(this.metricId); // TODO: Remove once all references are modified to pass the EROI ID instead of the title to avoid the need for mapping in this component and spec test modified.
      this.costCurrencyCode = this._currencyUomService.getDisplayedCurrency();

      if(this.metricId!=undefined){
        this.selectedItem = this.selectionMapping?.get(this.metricId)?.[1]
      }
      this._tvdService.tvdModalButtonAction$.subscribe(action => {
        // Only a save commits cost assumptions, so only a save may reveal the
        // "Default" value on the main page. Preview and close must not change it.
        if(action === ModalActions.SAVE){
          this.isDefaultCostAssumptionsVisible = true;
        }
      });
    }

    this.selectedDivision = this.filtersService.selectedDivision;
    this.selectedDataView = this.filtersService.selectedDataView;
    this.showEllipsis = this.isViewDetailsEnabled(this.selectedDivision);
    this.isCostAssumptionsEditable = this._costAssumptionsEligibilityResolver.resolveFromFilters(this.filters);

    this.currencySubscription = this._currencyUomService.displayedCurrency$.subscribe(() => {
      this.costCurrencyCode = this._currencyUomService.getDisplayedCurrency();
    });

    // generate tooltip id for this instance (use metricId if available)
    this.tooltipId = this.metricId && this.metricId.length ? `eroi-${this.metricId}` : `eroi-${Math.random().toString(36).substr(2,9)}`;

    // subscribe to shared tooltip service to show/hide based on current open id
    this.tooltipSub = this._tooltipService.currentOpen$.subscribe((openId: string | null) => {
      this.showTooltip = (openId === this.tooltipId);
    });

    // subscribe to when the value pillar metric selection limit is reached
    this.disableSelectionButtonSubscription = this._tvdService.selectedMetricsLimitObservable.subscribe(limitObj => {
      this.updateDisableSelectionButton();
    });
  }

  ngOnChanges(){
    if(this.metricId!=undefined){
      this.selectedItem = this.selectionMapping?.get(this.metricId)?.[1];
      this.addedToPres = this.selectionMapping?.get(this.metricId)?.[2];
      this.updateDisableSelectionButton();
    }
  }

  ngOnDestroy(): void {
    this.filtersSubscription?.unsubscribe();
    this.currencySubscription?.unsubscribe();
    this.tooltipSub?.unsubscribe();
    this.displayInRoundedNumberSubscription?.unsubscribe();
    this.showInEnterpriseTermsSubscription?.unsubscribe();
    this.disableSelectionButtonSubscription?.unsubscribe();
  }

  toggleSelectItem(title: string | undefined){
    //If the button is disabled, do nothing on click
    if(!this.disableSelectionButton){
      this.selectedItem = !this.selectedItem;
      if(this.selectedItem) {
        this.trackPlusSignClick(title);
      } else {
        this.trackMinusSignClick(title);
      }

      if(this.metricId!=undefined && this.selectionMapping!=undefined){
        let tempArray = this.selectionMapping.get(this.metricId);
        if(tempArray!=undefined){
          this.selectionMapping?.set(this.metricId, [this.data, this.selectedItem, tempArray[2]]);
        }
      }
    } 
  }

  /**
   * Disable the select button for adding to the presentation
   * if the limit of selected metrics per value pillar is reached and this.selectedItem==false
   */
  private updateDisableSelectionButton(){
    let valuePillarForMetric = EroiIdToPillarMapping.get(this.metricId as string);
    if(valuePillarForMetric) {
      let valuePillarLimitReached = this._tvdService.getSelectedMetricsLimit(valuePillarForMetric);
      this.disableSelectionButton = (this.selectedItem == false) && (valuePillarLimitReached == true);
    }
  }


  tvdDataIsEmptyString(tvdData:tvdData | undefined){
    if(tvdData!=undefined){ //should never be undefined since it is passed in from parent component
      if(tvdData.monetaryContribution=='' && tvdData.valueContribution =='') {
        return true;
      }
      return false;
    }
    return false;
  }


  trackPlusSignClick(title: string|undefined) {
    this._mixpanelService.track('TVD ' + title + ' Added', {
      Division: this.selectedDivision,
      DataView: this.selectedDataView,
    });
    this.selected.emit();
  }

  trackMinusSignClick(title: string|undefined) {
    this._mixpanelService.track('TVD ' + title + ' Removed', {
      Division: this.selectedDivision,
      DataView: this.selectedDataView,
      "Page Name": "Total Value Delivered",
      "TVD Metric Removed": title,
    });
    this.unselected.emit();
  }

  getWidth(metricValue:string | undefined){
    return metricValue === '' || metricValue === undefined ? true : false;
  }

  isBusinessMetric() {
    if(this.metricId)
      return this.eroiService.isBusinessMetric(this.metricId);
    return false;
  }

  toggleTooltip() {
    this._tooltipService.toggle(this.tooltipId);
  }

  onOutsideClick() {
    this._tooltipService.close();
  }

  ViewDetailsByLocation() {
    // close any open tooltip/ellipsis menu before navigating (deterministic close)
    try { this._tooltipService.close(); } catch (e) {}
    this._router.navigate(['/total-value-delivery-details'],
    {
      queryParams: {
        metricId: this.metricId,
        groupId: this.groupId,
        groupBy: TvdDrillDownGrouping.Location,
        metricName: this.eroiService.mapIdToTitle(this.metricId), // TODO: Consider passing the EROI ID instead of title to avoid the need for mapping in the details page.
      },
      state: {
        brandGroup: this.brandGroup,
        brands: this.brands
      }
    });
    this._mixpanelService.track('TVD location Drilldown Details Clicked', {
      MetricName: this.eroiService.mapIdToTitle(this.metricId), // TODO: Consider passing the EROI ID instead of title to avoid the need for mapping in the details page.
      TVDDollars: this._parseNumberPipe.transform(this.metricData.currentMonetaryContribution),
      TVDMetric: this._parseNumberPipe.transform(this.metricData.currentValueContribution),
    });
  }

  /**
   * modifiedValue param is either: modifiedMonetaryContribution or modifiedValueContribution
   * These values are originally zero, but if cost assumptions are applied then they are updated to the original monetary/value contribution.
   */
  isDefaultMetrics(title:string, modifiedValue:string, valueType : 'monetary' | 'value') {
    // No Charge Additional Services can have a zero monetary contribution, but cost assumptions can be modify it to be made non-zero
    if( this.metricId === eroiIds.NOCHARGEADDITIONALSERVICES && this.data !== undefined)
    {
      if(valueType === 'monetary'){
        let original = this._tvdService.getMonetaryContributionValue(eroiIds.NOCHARGEADDITIONALSERVICES);
        // If the original monetary contribution is zero, then check if the updated value is non-zero.
        // If the original monetary contribution is non-zero, then the modifiedValue must be greater than zero.
        this.isDefaultCostAssumptionsVisible = Number(original.replace(/,/g, ''))==0 ? Number(this.data.monetaryContribution?.replace(/,/g, '')) !==0 : Number(modifiedValue.replace(/,/g, '')) > 0;
        return this.defaultCostCenterMetrics.includes(title) && this.isDefaultCostAssumptionsVisible;
      }
      else{ //The value contribution can't be updated via cost assumptions, so no need to show a defalt value for it.
        return false;
      }
    }
    else
    {
      if (valueType === 'monetary') {
        this.isDefaultCostAssumptionsVisible = this.metricData.isMonetaryContributionModified;
      }
      else {
        this.isDefaultCostAssumptionsVisible = this.metricData.isValueContributionModified;
      }
      return this.defaultCostCenterMetrics.includes(title) && this.isDefaultCostAssumptionsVisible;
    }
  }  

  openSideDrawer() {
    this.isDrawerOpen = true;

    // Set interaction type context
    // TODO: Consider passing the EROI ID instead of title to avoid the need for mapping in this component and spec test modified.
    const title = this.eroiService.mapIdToTitle(this.metricId);
    this._tvdCostAssumptionsService.setInteractionContext('Individual', title || '');

    // Push metrics to mixpanel
    this._mixpanelService.trackEventWithFilters('Opened the Cost Assumptions Side Navbar', this.filters,
      "Total Value Delivery",
      { 'Interaction Type': 'Individual',
        'Metric Type': title // TODO: Consider passing the EROI ID instead of title to avoid the need for mapping in the details page.
      }
    );
  }

  closeSideDrawer() {
    this.isDrawerOpen = false;
  }
}
